'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { usePathname } from 'next/navigation';
import axios from 'axios';
import type { CartItem, Product } from '@/lib/pricing';
import type { StockInfo, RegionPool } from '@/hooks/useCart';

export interface CartContextType {
  // Cart items state
  cart: Record<string, CartItem>;
  setCart: React.Dispatch<React.SetStateAction<Record<string, CartItem>>>;
  clearCart: () => void;
  addToCart: (key: string, item: CartItem) => void;
  removeFromCart: (key: string) => void;
  updateQty: (key: string, qty: number) => void;

  // Player verification state
  uid: string;
  setUid: (uid: string) => void;
  validationResult: any;
  setValidationResult: (res: any) => void;
  purchasedLevels: Record<number, boolean>;
  setPurchasedLevels: (levels: Record<number, boolean>) => void;
  channelLevelUps: any[];
  setChannelLevelUps: (packs: any[]) => void;
  hasOneTimeOffer: boolean | null;
  setHasOneTimeOffer: (val: boolean | null) => void;
  selectedTabRegion: 'SG' | 'ID';
  setSelectedTabRegion: (region: 'SG' | 'ID') => void;

  // Global catalog state
  products: Product[];
  stockAvailability: Record<string, StockInfo>;
  regionPools: Record<string, RegionPool>;
  charges: any;
  paymentConfig: any;
  loadingCatalog: boolean;
  activeOffers: Record<string, any>;

  // Hydration helper for SSR
  hydrateCatalog: (data: {
    products?: Product[];
    stockAvailability?: Record<string, StockInfo>;
    regionPools?: Record<string, RegionPool>;
    charges?: any;
    paymentConfig?: any;
    activeOffers?: Record<string, any>;
  }) => void;
  mergeProducts: (products: Product[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // ── Cart State ──
  const [cart, setCart] = useState<Record<string, CartItem>>({});

  // ── Player State ──
  const [uid, setUid] = useState<string>('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [purchasedLevels, setPurchasedLevels] = useState<
    Record<number, boolean>
  >({});
  const [channelLevelUps, setChannelLevelUps] = useState<any[]>([]);
  const [hasOneTimeOffer, setHasOneTimeOffer] = useState<boolean | null>(null);
  const [selectedTabRegion, setSelectedTabRegion] = useState<'SG' | 'ID'>('SG');

  // ── Global Catalog State ──
  const [products, setProducts] = useState<Product[]>([]);
  const [stockAvailability, setStockAvailability] = useState<
    Record<string, StockInfo>
  >({});
  const [regionPools, setRegionPools] = useState<Record<string, RegionPool>>(
    {},
  );
  const [charges, setCharges] = useState<any>({
    processingChargeEnabled: false,
    processingChargePercent: 0,
  });
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [activeOffers, setActiveOffers] = useState<Record<string, any>>({});
  const [loadingCatalog, setLoadingCatalog] = useState<boolean>(true);
  const [hasHydrated, setHasHydrated] = useState<boolean>(false);

  // ── 1. LocalStorage Read on Mount ──
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Clean up any previously stored player verification so user always verifies fresh on visit
    try {
      localStorage.removeItem('topupbot_player_uid');
      localStorage.removeItem('topupbot_player_val_res');
      localStorage.removeItem('topupbot_player_purchased_levels');
      localStorage.removeItem('topupbot_player_channel_level_ups');
      localStorage.removeItem('topupbot_has_one_time_offer');
    } catch (_) {}

    const savedCart = localStorage.getItem('topupbot_cart');
    const savedRegion = localStorage.getItem('topupbot_player_region');

    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse saved cart', e);
      }
    }
    if (savedRegion === 'SG' || savedRegion === 'ID') {
      setSelectedTabRegion(savedRegion);
    }
  }, []);

  // ── 2. LocalStorage Write on State Change ──
  useEffect(() => {
    if (Object.keys(cart).length > 0) {
      localStorage.setItem('topupbot_cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('topupbot_cart');
    }
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('topupbot_player_region', selectedTabRegion);
  }, [selectedTabRegion]);

  // Synchronize region tab when validation result changes
  useEffect(() => {
    if (
      validationResult &&
      (validationResult.valid || validationResult.nickname)
    ) {
      const reg = (validationResult.region || 'SG').toUpperCase();
      setSelectedTabRegion(reg === 'ID' ? 'ID' : 'SG');
    }
  }, [validationResult]);

  // ── 3. Hydration Helper for SSR pages ──
  const hydrateCatalog = useCallback(
    (data: {
      products?: Product[];
      stockAvailability?: Record<string, StockInfo>;
      regionPools?: Record<string, RegionPool>;
      charges?: any;
      paymentConfig?: any;
      activeOffers?: Record<string, any>;
    }) => {
      if (data.products) setProducts(data.products);
      if (data.stockAvailability) setStockAvailability(data.stockAvailability);
      if (data.regionPools) setRegionPools(data.regionPools);
      if (data.charges) setCharges(data.charges);
      if (data.paymentConfig) setPaymentConfig(data.paymentConfig);
      if (data.activeOffers) setActiveOffers(data.activeOffers);
      setLoadingCatalog(false);
      setHasHydrated(true);
    },
    [],
  );

  const mergeProducts = useCallback((incoming: Product[]) => {
    setProducts((current) => {
      const merged = new Map(current.map((product) => [product.id, product]));
      incoming.forEach((product) => merged.set(product.id, product));
      return Array.from(merged.values());
    });
  }, []);

  // ── 4. Fallback client-side catalog fetcher ──
  useEffect(() => {
    if (hasHydrated) return;

    const API_BASE = '/api';
    let isMounted = true;

    const fetchCatalogData = async () => {
      try {
        setLoadingCatalog(true);
        const [productsRes, stockRes, chargesRes, paymentRes, offersRes] =
          await Promise.all([
            axios.get(`${API_BASE}/public/products`),
            axios.get(`${API_BASE}/public/stock-availability`),
            axios.get(`${API_BASE}/public/charges`),
            axios.get(`${API_BASE}/public/payment-config`),
            axios.get(`${API_BASE}/public/active-offers`),
          ]);

        if (!isMounted) return;

        // Extract products
        const fetchedProducts =
          productsRes.data?.data || productsRes.data || [];
        setProducts(fetchedProducts);

        // Extract stock
        const stockData = stockRes.data?.data || stockRes.data || {};
        if (stockData.products) {
          const map: Record<string, StockInfo> = {};
          for (const p of stockData.products) {
            map[p.productId] = {
              isAvailable: p.isAvailable,
              shellsNeeded: p.shellsNeeded,
              shellsSg: p.shellsSg ?? 0,
              shellsMy: p.shellsMy ?? 0,
              shellsId: p.shellsId ?? 0,
              maxShellsInSingleAccount: p.maxShellsInSingleAccount,
              totalAvailableShells: p.totalAvailableShells,
              defaultRegion: p.defaultRegion,
              maxQty: p.maxQty ?? (p.isAvailable ? 99 : 0),
              isBundle: p.isBundle ?? false,
              constituents: p.constituents ?? undefined,
            };
          }
          setStockAvailability(map);
        }

        if (stockData.summary?.regions) {
          const pools: Record<string, RegionPool> = {};
          for (const [region, info] of Object.entries(
            stockData.summary.regions as Record<string, any>,
          )) {
            pools[region] = {
              accounts: info.accounts ?? 0,
              totalShells: info.totalShells ?? 0,
              maxAccountShells: info.maxAccountShells ?? 0,
              accountShells: info.accountShells ?? [],
              publicReserved: info.publicReserved ?? 0,
            };
          }
          setRegionPools(pools);
        }

        // Extract charges
        const fetchedCharges =
          chargesRes.data?.data ||
          chargesRes.data || {
            processingChargeEnabled: false,
            processingChargePercent: 0,
          };
        setCharges(fetchedCharges);

        // Extract payment config
        const fetchedPaymentConfig =
          paymentRes.data?.data || paymentRes.data || null;
        setPaymentConfig(fetchedPaymentConfig);

        // Extract active offers
        const fetchedOffers = offersRes.data?.data || offersRes.data || [];
        const offerMap: Record<string, any> = {};
        if (Array.isArray(fetchedOffers)) {
          for (const offer of fetchedOffers) {
            if (offer.products && Array.isArray(offer.products)) {
              for (const op of offer.products) {
                offerMap[op.productId] = op;
              }
            }
          }
        }
        setActiveOffers(offerMap);
      } catch (err) {
        console.error(
          'Failed to fetch fallback catalog data client-side:',
          err,
        );
      } finally {
        if (isMounted) setLoadingCatalog(false);
      }
    };

    fetchCatalogData();

    return () => {
      isMounted = false;
    };
  }, [hasHydrated]);

  // Set up periodic stock update polling (every 30 seconds) across all pages
  useEffect(() => {
    const API_BASE = '/api';
    let isMounted = true;

    const pollStock = async () => {
      try {
        const stockRes = await axios.get(
          `${API_BASE}/public/stock-availability`,
        );
        if (!isMounted) return;
        const stockData = stockRes.data?.data || stockRes.data || {};
        if (stockData.products) {
          const map: Record<string, StockInfo> = {};
          for (const p of stockData.products) {
            map[p.productId] = {
              isAvailable: p.isAvailable,
              shellsNeeded: p.shellsNeeded,
              shellsSg: p.shellsSg ?? 0,
              shellsMy: p.shellsMy ?? 0,
              shellsId: p.shellsId ?? 0,
              maxShellsInSingleAccount: p.maxShellsInSingleAccount,
              totalAvailableShells: p.totalAvailableShells,
              defaultRegion: p.defaultRegion,
              maxQty: p.maxQty ?? (p.isAvailable ? 99 : 0),
              isBundle: p.isBundle ?? false,
              constituents: p.constituents ?? undefined,
            };
          }
          setStockAvailability(map);
        }

        if (stockData.summary?.regions) {
          const pools: Record<string, RegionPool> = {};
          for (const [region, info] of Object.entries(
            stockData.summary.regions as Record<string, any>,
          )) {
            pools[region] = {
              accounts: info.accounts ?? 0,
              totalShells: info.totalShells ?? 0,
              maxAccountShells: info.maxAccountShells ?? 0,
              accountShells: info.accountShells ?? [],
              publicReserved: info.publicReserved ?? 0,
            };
          }
          setRegionPools(pools);
        }
      } catch (err) {
        console.error('Periodic stock update failed:', err);
      }
    };

    if (Object.keys(stockAvailability).length === 0) {
      pollStock();
    }

    const stockPollInterval = setInterval(pollStock, 30000);

    return () => {
      isMounted = false;
      clearInterval(stockPollInterval);
    };
  }, []);

  // ── 5. Cart Mutation Handlers ──
  const clearCart = useCallback(() => setCart({}), []);

  const addToCart = useCallback(
    (key: string, item: CartItem) =>
      setCart((prev) => ({ ...prev, [key]: item })),
    [],
  );

  const removeFromCart = useCallback(
    (key: string) =>
      setCart((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      }),
    [],
  );

  const updateQty = useCallback(
    (key: string, qty: number) =>
      setCart((prev) => {
        if (!prev[key]) return prev;
        return { ...prev, [key]: { ...prev[key], qty } };
      }),
    [],
  );

  const value: CartContextType = {
    cart,
    setCart,
    clearCart,
    addToCart,
    removeFromCart,
    updateQty,
    uid,
    setUid,
    validationResult,
    setValidationResult,
    purchasedLevels,
    setPurchasedLevels,
    channelLevelUps,
    setChannelLevelUps,
    hasOneTimeOffer,
    setHasOneTimeOffer,
    selectedTabRegion,
    setSelectedTabRegion,
    products,
    stockAvailability,
    regionPools,
    charges,
    paymentConfig,
    activeOffers,
    loadingCatalog,
    hydrateCatalog,
    mergeProducts,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
}
